import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import NewNoteDialog from "@/components/encounter/NewNoteDialog";
import ChartFilterBar from "@/components/patient/ChartFilterBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PAT } from "@/constants/testIds";
import { ENCOUNTER_READ_ONLY_REASON, isEncounterReadOnly } from "@/lib/encounterApi";
import EncounterReadOnlyHint from "@/components/encounter/EncounterReadOnlyHint";
import { createNoteIdentity, listNoteTypes, listPatientNotes } from "@/lib/noteApi";
import {
  EMPTY_NOTE_FILTERS,
  NOTE_FILTER_LABELS,
  NOTE_STATUS_FILTER_OPTIONS,
  chartNoteRows,
  filterNoteRows,
} from "@/lib/patientChartClinical";

const STATUS_STYLE = {
  signed: "bg-emerald-100 text-emerald-800",
  draft: "bg-amber-100 text-amber-800",
};

export default function NotesWorkspace({
  patientId,
  encounters = [],
  currentUser,
  onOpenNote,
  accessReadOnly = false,
}) {
  const [filters, setFilters] = useState(EMPTY_NOTE_FILTERS);
  const [notes, setNotes] = useState([]);
  const [noteTypes, setNoteTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const [encounterId, setEncounterId] = useState(
    encounters.find((enc) => !isEncounterReadOnly(enc))?.id || encounters[0]?.id || "",
  );

  useEffect(() => {
    setEncounterId((current) => {
      const currentEnc = encounters.find((enc) => enc.id === current);
      if (currentEnc && !isEncounterReadOnly(currentEnc)) return current;
      return encounters.find((enc) => !isEncounterReadOnly(enc))?.id || encounters[0]?.id || "";
    });
  }, [encounters]);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([listPatientNotes(patientId), listNoteTypes().catch(() => [])])
      .then(([items, types]) => {
        if (cancelled) return;
        setNotes(items);
        setNoteTypes(types);
      })
      .catch(() => {
        if (cancelled) return;
        setNotes([]);
        toast.error("Failed to load notes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const rows = useMemo(
    () => filterNoteRows(chartNoteRows(notes, encounters, currentUser), filters.keyword, filters.status),
    [notes, encounters, currentUser, filters.keyword, filters.status],
  );

  const noWritableEncounter = encounters.length > 0 && encounters.every((enc) => isEncounterReadOnly(enc));

  const openNewNote = () => {
    if (!encounters.length) {
      toast.info("Create an encounter before adding a note.");
      return;
    }
    if (noWritableEncounter) {
      toast.info(ENCOUNTER_READ_ONLY_REASON);
      return;
    }
    setNewNoteOpen(true);
  };

  const createNote = async ({ noteTypeId, title, encounterId: selectedId }) => {
    const selectedEncounterId = selectedId || encounterId || encounters[0]?.id;
    if (!selectedEncounterId) {
      toast.info("Create an encounter before adding a note.");
      return;
    }
    const target = encounters.find((enc) => enc.id === selectedEncounterId);
    if (isEncounterReadOnly(target)) {
      toast.info(ENCOUNTER_READ_ONLY_REASON);
      return;
    }
    try {
      const created = await createNoteIdentity({
        encounterId: selectedEncounterId,
        noteTypeId,
        title,
      });
      setNotes((prev) => [created, ...prev.filter((note) => note.id !== created.id)]);
      setNewNoteOpen(false);
      toast.success("Note created.");
      onOpenNote?.(created);
    } catch {
      toast.error("Could not create the Note.");
    }
  };

  return (
    <section
      data-testid={PAT.chartNotesPanel}
      className="emr-chart-dense rounded-xl border border-slate-200 bg-[#FFFFFF] p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-900">Notes</h2>
        {accessReadOnly ? null : (
        <EncounterReadOnlyHint locked={noWritableEncounter}>
          <Button
            type="button"
            size="sm"
            data-testid={PAT.chartNewNote}
            disabled={noWritableEncounter}
            onClick={openNewNote}
          >
            New Note
          </Button>
        </EncounterReadOnlyHint>
        )}
      </div>
      <ChartFilterBar
        testId={PAT.chartNotesFilters}
        labels={NOTE_FILTER_LABELS}
        searchValue={filters.keyword}
        onSearchChange={(keyword) => setFilters((current) => ({ ...current, keyword }))}
        searchTestId={PAT.chartNotesSearch}
        selects={{
          Status: {
            value: filters.status,
            onChange: (status) => setFilters((current) => ({ ...current, status })),
            options: NOTE_STATUS_FILTER_OPTIONS,
            testId: PAT.chartNotesStatusFilter,
          },
        }}
      />
      {loading ? (
        <p className="py-6 text-center text-slate-500">Loading notes…</p>
      ) : rows.length ? (
        <div className="-mx-1 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead>
                <TableHead>Encounter #</TableHead>
                <TableHead>Note Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Author</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`${PAT.chartNoteRow}-${row.id}`}
                  className={onOpenNote ? "cursor-pointer" : undefined}
                  onClick={() => onOpenNote?.(row.note)}
                >
                  <TableCell className="whitespace-nowrap text-slate-700">{row.date}</TableCell>
                  <TableCell>
                    <span className="font-fhir text-[#1E79E8]">{row.encounterNumber}</span>
                  </TableCell>
                  <TableCell className="text-slate-700">{row.noteType}</TableCell>
                  <TableCell className="text-slate-700">{row.title || "—"}</TableCell>
                  <TableCell>
                    <Badge className={`emr-chart-dense__badge ${STATUS_STYLE[row.status] || "bg-slate-200 text-slate-600"} hover:opacity-100`}>
                      {row.statusLabel}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-700">{row.author}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="py-6 text-center text-slate-500">No notes available.</p>
      )}

      {accessReadOnly ? null : (
      <NewNoteDialog
        open={newNoteOpen}
        onOpenChange={setNewNoteOpen}
        noteTypes={noteTypes}
        encounters={encounters}
        encounterId={encounterId}
        onEncounterIdChange={setEncounterId}
        onCreate={createNote}
      />
      )}
    </section>
  );
}
